import { useState } from 'react'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const TABS = [
  { key: 'dinner', label: 'Dinner' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'snacks', label: 'Snacks' },
  { key: 'misc', label: 'Misc' },
]

export default function BundlePicker({ bundles, defaultTab, onAdd, onClose }) {
  const [activeTab, setActiveTab] = useState(defaultTab || 'dinner')
  const [selected, setSelected] = useState(null)
  const [selectedDays, setSelectedDays] = useState([])
  const [allWeek, setAllWeek] = useState(false)

  function switchTab(tab) {
    setActiveTab(tab)
    setSelected(null)
    setSelectedDays([])
    setAllWeek(false)
  }

  function toggleDay(day) {
    if (allWeek) {
      setAllWeek(false)
      setSelectedDays([day])
      return
    }
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    )
  }

  function toggleAllWeek() {
    if (allWeek) {
      setAllWeek(false)
      setSelectedDays([])
    } else {
      setAllWeek(true)
      setSelectedDays(DAYS)
    }
  }

  const visibleBundles = bundles.filter(b => b.category === activeTab)

  return (
    <>
      <div style={styles.backdrop} onClick={onClose} />
      <div style={styles.sheet}>
        <div style={styles.handle} />
        <div style={styles.sheetTitle}>Add a bundle</div>

        <div style={styles.tabRow}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }}
              onClick={() => switchTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={styles.bundleList}>
          {visibleBundles.length === 0 ? (
            <div style={styles.empty}>No {activeTab} bundles saved yet.</div>
          ) : (
            visibleBundles.map(bundle => (
              <div
                key={bundle.id}
                style={{ ...styles.bundleRow, ...(selected?.id === bundle.id ? styles.bundleRowSelected : {}) }}
                onClick={() => setSelected(bundle)}
              >
                <div style={{ ...styles.bundleEmoji, background: bundle.bg || '#f9f9f7' }}>
                  {bundle.emoji}
                </div>
                <div style={styles.bundleInfo}>
                  <div style={styles.bundleName}>{bundle.name}</div>
                  <div style={styles.bundleMeta}>{bundle.items} items · ~${bundle.price}</div>
                </div>
                {selected?.id === bundle.id && <span style={styles.check}>✓</span>}
              </div>
            ))
          )}
        </div>

        {selected && (
          <div style={styles.daySection}>
            <div style={styles.dayLabel}>Days (optional)</div>
            <div style={styles.dayRow}>
              {DAYS.map(day => (
                <button
                  key={day}
                  style={{ ...styles.dayBtn, ...(selectedDays.includes(day) && !allWeek ? styles.dayBtnActive : {}) }}
                  onClick={() => toggleDay(day)}
                >
                  {day}
                </button>
              ))}
              <button
                style={{ ...styles.allWeekBtn, ...(allWeek ? styles.allWeekBtnActive : {}) }}
                onClick={toggleAllWeek}
              >
                Every day
              </button>
            </div>
          </div>
        )}

        <button
          style={{ ...styles.addBtn, ...(!selected ? styles.addBtnDim : {}) }}
          onClick={() => selected && onAdd(selected.id, selectedDays)}
          disabled={!selected}
        >
          Add to plan
        </button>
      </div>
    </>
  )
}

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100,
  },
  sheet: {
    position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: '430px', background: 'white',
    borderRadius: '20px 20px 0 0', padding: '12px 20px 32px',
    zIndex: 101, display: 'flex', flexDirection: 'column', gap: '12px',
    maxHeight: '80vh', fontFamily: "'DM Sans', sans-serif",
  },
  handle: {
    width: '36px', height: '4px', borderRadius: '2px',
    background: '#e0e0e0', margin: '0 auto 4px', flexShrink: 0,
  },
  sheetTitle: {
    fontSize: '16px', fontWeight: '500', color: '#1a1a18',
    textAlign: 'center', flexShrink: 0,
  },
  tabRow: {
    display: 'flex', gap: '6px', overflowX: 'auto', flexShrink: 0,
    paddingBottom: '2px',
  },
  tab: {
    padding: '6px 14px', borderRadius: '20px', border: '0.5px solid #ddd',
    background: '#f9f9f7', fontSize: '12px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: '#1D9E75', borderColor: '#1D9E75', color: 'white', fontWeight: '500',
  },
  bundleList: { overflowY: 'auto', flex: 1, minHeight: 0 },
  empty: { fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '24px 20px' },
  bundleRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '9px 10px', borderRadius: '12px', cursor: 'pointer', marginBottom: '2px',
  },
  bundleRowSelected: { background: '#f4fbf8' },
  bundleEmoji: {
    width: '38px', height: '38px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '18px', flexShrink: 0,
  },
  bundleInfo: { flex: 1 },
  bundleName: { fontSize: '13px', fontWeight: '500', color: '#1a1a18' },
  bundleMeta: { fontSize: '11px', color: '#888' },
  check: { fontSize: '14px', color: '#1D9E75', fontWeight: '600' },
  daySection: { flexShrink: 0 },
  dayLabel: {
    fontSize: '11px', fontWeight: '600', color: '#aaa',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px',
  },
  dayRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  dayBtn: {
    padding: '6px 10px', borderRadius: '20px', border: '0.5px solid #ddd',
    background: '#f9f9f7', fontSize: '12px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  dayBtnActive: {
    background: '#1D9E75', borderColor: '#1D9E75', color: 'white', fontWeight: '500',
  },
  allWeekBtn: {
    padding: '6px 10px', borderRadius: '20px', border: '0.5px solid #ddd',
    background: '#f9f9f7', fontSize: '12px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  allWeekBtnActive: {
    background: '#1a1a18', borderColor: '#1a1a18', color: 'white', fontWeight: '500',
  },
  addBtn: {
    padding: '13px', background: '#1D9E75', color: 'white', border: 'none',
    borderRadius: '12px', fontSize: '14px', fontWeight: '500',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
  },
  addBtnDim: { background: '#ccc', cursor: 'default' },
}
